/**
 * S21 — renders a view's `matrix` (DSM) representation from the engine's
 * `MatrixData` (`computeMatrixData`). Pure over the data model: a rows×cols grid
 * where a filled cell means row→col couples (a call edge). An empty cell on
 * scaffold-able (key) axes is clickable to scaffold `row > col` — the container
 * supplies `onScaffold` (routes through `scaffoldEdgeEdit`). A clustered DSM
 * additionally shades each cycle block on the diagonal and flags its
 * above-diagonal feedback marks (the cycle payload), with a legend below.
 *
 * ## User flow (what the DSM is FOR)
 * The DSM answers "does row depend on column, and where does the graph feed
 * back on itself?" — a square module×module coupling grid. The reader scans:
 *   1. the ROW header column (sticky at left) and COLUMN header row (sticky at
 *      top) to fix the shared axis. Column labels are ROTATED vertical so they
 *      read cleanly over the narrow (~22px) cells — a horizontal label would
 *      overflow into its neighbours at any real N×N. Both header bands stay
 *      pinned while the body scrolls, so a large matrix never loses its axes;
 *   2. a CELL: a filled `●` means row→col couples (hover it for the deriving
 *      process). To TRACE a dependency without ambiguity, hovering any cell,
 *      row header, or column header lights the full cross — that row's header,
 *      that column's header, and every cell along both — so the eye follows a
 *      lone dot straight back to its two labels even deep in a dense grid;
 *   3. the DIAGONAL (module vs itself), muted so the triangle split reads;
 *   4. when clustered, the shaded cycle BOXES on the diagonal group each
 *      strongly-connected component, and the amber FEEDBACK marks above the
 *      diagonal are the back-edges those cycles close — the structure a plain
 *      edge list hides. A legend (footer) keys both;
 *   5. on the ungrouped key axis an EMPTY off-diagonal cell offers a `+` to
 *      scaffold `row > col` in place; grouped (getter) axes are display-only.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { useTranslation } from "../i18n";
import { FOCUS_GROUND_ALPHA } from "@archlang/scene/board";
import { useWindow } from "./virtualization/windowing.js";
import { SpacerRow, SpacerCell } from "./virtualization/Spacers.js";

/**
 * The render contract — structurally satisfied by the engine's `MatrixData`
 * (`computeMatrixData`). Kept local so the component doesn't couple to the engine
 * barrel (see ViewTableView).
 */
export interface MatrixViewModel {
  readonly axisRows: readonly string[];
  readonly axisCols: readonly string[];
  readonly rowsGrouped: boolean;
  readonly colsGrouped: boolean;
  readonly cells: readonly (readonly {
    readonly present: boolean;
    readonly deriving?: string;
    readonly feedback?: boolean;
  }[])[];
  /** Cycle blocks to shade on the diagonal — present only when clustered. */
  readonly blocks?: readonly {
    readonly members: readonly number[];
    readonly isCycle: boolean;
  }[];
}

export interface ViewMatrixViewProps {
  /** The view's `focus` set (origin-qualified keys). Lights the focused
   *  module's BAND — see the note at the band computation for why a DSM cannot
   *  use the board's both-endpoints rule, and when the band declines to engage. */
  readonly focus?: ReadonlySet<string>;
  readonly data: MatrixViewModel;
  /** Scaffold `row > col` for an empty cell (only offered on key axes). */
  readonly onScaffold?: (rowKey: string, colKey: string) => void;
}

/** The cross-hair target: an axis position, either coordinate free (`-1`). */
interface Hover {
  readonly r: number;
  readonly c: number;
}

export function ViewMatrixView({ data, onScaffold, focus }: ViewMatrixViewProps): ReactNode {
  const { t } = useTranslation("nav");
  const menu = useContextMenu();
  const keyAxes = data.rowsGrouped === false && data.colsGrouped === false;

  // A cell's right-click menu: copy its address (`row → col`) and its two axis
  // labels (always safe via the clipboard). "Scaffold edge" is offered ONLY when
  // the existing scaffold handler applies to THAT cell — the same predicate the
  // in-cell `+` button uses (empty, key axes, off-diagonal, handler wired).
  const cellMenu = (r: string, c: string, present: boolean): readonly ContextMenuItem[] => {
    const scaffoldable = !present && keyAxes && !!onScaffold && r !== c;
    return [
      { label: t("menu.copyAddress"), onClick: () => void navigator.clipboard?.writeText(`${r} → ${c}`) },
      { label: `${t("menu.copyName")}: ${r}`, onClick: () => void navigator.clipboard?.writeText(r) },
      { label: `${t("menu.copyName")}: ${c}`, onClick: () => void navigator.clipboard?.writeText(c) },
      ...(scaffoldable
        ? [{ label: t("menu.scaffoldEdge"), separator: true, onClick: () => onScaffold!(r, c) }]
        : []),
    ];
  };

  // Cross-hair trace: the row/col currently under the pointer. Hovering a cell
  // fixes both coordinates; hovering a header fixes one (the other is -1, so
  // only that header's whole line lights). Cleared on leaving the table.
  const [hover, setHover] = useState<Hover | null>(null);

  // 2D windowing — the DSM is quadratic (N×N cells), so both axes window above
  // the threshold: only the visible rows × visible cols mount, spacer rows/cells
  // absorb the rest. Cross-hair, cycle blocks, and the diagonal all key off
  // ABSOLUTE (ri, ci) indices — unchanged — because the slices carry the real
  // indices, not slice-relative ones. A single `colWin` feeds BOTH the header row
  // and every body row, so the rotated column headers stay aligned with cells.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [cell, setCell] = useState({ w: 23, h: 23 });
  const rowWin = useWindow(scrollRef, data.axisRows.length, { estimateSize: cell.h });
  const colWin = useWindow(scrollRef, data.axisCols.length, { estimateSize: cell.w, horizontal: true });
  // Measure a real cell's occupied size once it's painted (getBoundingClientRect
  // is border-box, and with border-spacing:0 + no left/top borders that IS the
  // stride) — box-sizing-agnostic, so the estimate matches layout exactly and the
  // spacers never drift.
  useLayoutEffect(() => {
    const td = scrollRef.current?.querySelector<HTMLElement>("tbody td:not(.arch-spacer)");
    if (!td) return;
    const r = td.getBoundingClientRect();
    const w = Math.round(r.width);
    const h = Math.round(r.height);
    if (w > 0 && h > 0 && (w !== cell.w || h !== cell.h)) setCell({ w, h });
  }, [cell.w, cell.h, data.axisRows.length, data.axisCols.length]);
  // colSpan upper bound for the full-width vertical spacer rows.
  const spannedCols = colWin.indices.length + 3;

  // Ordered-axis position → cycle-block id (-1 = none), so a cell whose row and
  // column share a cycle block reads as a shaded diagonal box.
  const blockOf = data.axisRows.map(() => -1);
  let hasCycle = false;
  (data.blocks ?? []).forEach((b, bi) => {
    if (!b.isCycle) return;
    hasCycle = true;
    for (const m of b.members) blockOf[m] = bi;
  });
  const hasFeedback = data.cells.some((row) => row.some((c) => c.feedback));

  // View `focus` on a DSM: the focused module's BAND (its row + its column),
  // not the board's both-endpoints rule — a cell IS a call edge and the
  // diagonal is not filled, so both-endpoints would mute the entire matrix and
  // focus would vanish exactly where it was asked for.
  //
  // Only PRESENT cells stay figure: lighting a whole band would emphasise
  // ABSENCE. And a GROUPED axis (`axis @@team`) is excluded outright — its
  // labels are aspect VALUES, not element keys, so a "match" there would be a
  // coincidence of spelling (a module named like one of the values) rather than
  // the focused element. Reading `rowsGrouped`/`colsGrouped` says that
  // directly; inferring it from "no label happened to match" left that
  // collision live.
  const bandRows = new Set<number>();
  const bandCols = new Set<number>();
  if (focus && focus.size > 0) {
    if (!data.rowsGrouped) data.axisRows.forEach((r, i) => focus.has(r) && bandRows.add(i));
    if (!data.colsGrouped) data.axisCols.forEach((c, i) => focus.has(c) && bandCols.add(i));
  }
  const banded = bandRows.size > 0 || bandCols.size > 0;
  const groundStyle = { opacity: FOCUS_GROUND_ALPHA } as const;
  const cellGround = (ri: number, ci: number, present: boolean): boolean =>
    banded && !(present && (bandRows.has(ri) || bandCols.has(ci)));
  const headGround = (band: ReadonlySet<number>, i: number): boolean => banded && !band.has(i);

  return (
    <div className="arch-view-matrix">
      <div className="arch-matrix-scroll" ref={scrollRef}>
        <table className="arch-matrix-table" onMouseLeave={() => setHover(null)}>
          <thead>
            <tr>
              <th className="arch-matrix-corner arch-matrix-th" />
              <SpacerCell as="th" width={colWin.before} />
              {colWin.indices.map((ci) => {
                const c = data.axisCols[ci];
                if (c === undefined) return null;
                return (
                  <th
                    key={c}
                    scope="col"
                    className="arch-matrix-colhead arch-matrix-th"
                    title={c}
                    data-index={ci}
                    {...(hover?.c === ci ? { "data-active": "" } : {})}
                    {...(headGround(bandCols, ci) ? { "data-focus": "ground" as const, style: groundStyle } : {})}
                    onMouseEnter={() => setHover({ r: -1, c: ci })}
                  >
                    <span className="arch-matrix-colhead-label">{c}</span>
                  </th>
                );
              })}
              <SpacerCell as="th" width={colWin.after} />
            </tr>
          </thead>
          <tbody>
            <SpacerRow height={rowWin.before} cols={spannedCols} />
            {rowWin.indices.map((ri) => {
              const r = data.axisRows[ri];
              if (r === undefined) return null;
              return (
                <tr key={r} data-index={ri}>
                  <th
                    scope="row"
                    className="arch-matrix-rowhead arch-matrix-th"
                    title={r}
                    {...(hover?.r === ri ? { "data-active": "" } : {})}
                    {...(headGround(bandRows, ri) ? { "data-focus": "ground" as const, style: groundStyle } : {})}
                    onMouseEnter={() => setHover({ r: ri, c: -1 })}
                  >
                    {r}
                  </th>
                  <SpacerCell width={colWin.before} />
                  {colWin.indices.map((ci) => {
                    const c = data.axisCols[ci];
                    if (c === undefined) return null;
                    const cell = data.cells[ri]?.[ci];
                    const present = cell?.present ?? false;
                    const diagonal = keyAxes && ri === ci;
                    const inCycle = blockOf[ri]! >= 0 && blockOf[ri] === blockOf[ci];
                    const cross = hover !== null && (hover.r === ri || hover.c === ci);
                    const key = `${r}→${c}`;
                    if (!present && keyAxes && onScaffold && r !== c) {
                      return (
                        <td
                          key={ci}
                          data-present="false"
                          {...(inCycle ? { "data-cycle": "" } : {})}
                          {...(cross ? { "data-cross": "" } : {})}
                          {...(cellGround(ri, ci, false) ? { "data-focus": "ground" as const, style: groundStyle } : {})}
                          className="arch-matrix-empty"
                          onMouseEnter={() => setHover({ r: ri, c: ci })}
                          onContextMenu={(e) => menu.open(e, cellMenu(r, c, present))}
                        >
                          <button
                            type="button"
                            aria-label={`scaffold ${r} to ${c}`}
                            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                            onClick={() => onScaffold(r, c)}
                          >
                            +
                          </button>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={ci}
                        data-present={present}
                        {...(cell?.feedback ? { "data-feedback": "" } : {})}
                        {...(inCycle ? { "data-cycle": "" } : {})}
                        {...(diagonal ? { "data-diagonal": "" } : {})}
                        {...(cross ? { "data-cross": "" } : {})}
                        {...(cellGround(ri, ci, present) ? { "data-focus": "ground" as const, style: groundStyle } : {})}
                        title={present ? cell?.deriving ?? key : undefined}
                        className={present ? "arch-matrix-on" : "arch-matrix-off"}
                        onMouseEnter={() => setHover({ r: ri, c: ci })}
                        onContextMenu={(e) => menu.open(e, cellMenu(r, c, present))}
                      >
                        {present ? "●" : ""}
                      </td>
                    );
                  })}
                  <SpacerCell width={colWin.after} />
                </tr>
              );
            })}
            <SpacerRow height={rowWin.after} cols={spannedCols} />
          </tbody>
        </table>
      </div>
      {(hasCycle || hasFeedback) && (
        <div className="arch-matrix-legend">
          {hasCycle && (
            <span className="arch-matrix-legend-item">
              <span className="arch-matrix-swatch" data-cycle="" />
              cycle
            </span>
          )}
          {hasFeedback && (
            <span className="arch-matrix-legend-item">
              <span className="arch-matrix-swatch" data-feedback="" />
              feedback
            </span>
          )}
        </div>
      )}
      {menu.node}
    </div>
  );
}
